import React from "react";
import Card from "./Card";

// The card every Security Operations chart sits in: title, optional subtitle, the chart, a legend
// when there are two or more series, and a "View as table" with the plotted numbers. An empty
// chart shows only its message. `Card` does not forward extra props, so the test id sits on a
// wrapper div. `height` is the plot's Tailwind height class (h-56 by default); `aside` sits at the
// right of the title (a total, a badge). With `onLegendHover` the legend entries respond to pointer and
// keyboard focus (label, or null on leave) and `activeLegend` fades the other entries.
// A blank chart: the plot area at its usual height, a dashed outline with faint grid lines, and why it
// is empty in the middle. Keeps the page's layout whole when a source is not connected or has no data,
// without drawing a zero that could read as "nothing found".
export const SocEmptyFrame = ({ height = "h-56", testId, children }) => (
  <div className={`relative mt-3 ${height}`} data-testid={testId}>
    <div
      className="absolute inset-0 rounded-lg border border-dashed border-border"
      style={{
        backgroundImage:
          "repeating-linear-gradient(to bottom, transparent 0, transparent calc(25% - 1px), var(--chart-grid) calc(25% - 1px), var(--chart-grid) 25%)",
      }}
      aria-hidden="true"
    />
    <div className="absolute inset-0 flex items-center justify-center p-4">
      <p className="max-w-xs rounded-full bg-theme-secondary px-3 py-1.5 text-center text-sm text-content-secondary">{children}</p>
    </div>
  </div>
);

const SocChartCard = ({
  testId, title, subtitle, legend, table, empty, emptyText, children, height = "h-56", aside, activeLegend = null, onLegendHover,
}) => (
  <div data-testid={testId}>
    <Card className="h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-content-primary font-semibold">{title}</h3>
          {subtitle && <p className="text-xs text-content-muted">{subtitle}</p>}
        </div>
        {aside}
      </div>

      {empty ? (
        <SocEmptyFrame height={height} testId={testId ? `${testId}-empty` : undefined}>{emptyText}</SocEmptyFrame>
      ) : (
        <>
          <div className={`mt-3 ${height}`}>{children}</div>
          {legend && legend.length >= 2 && (
            <ul aria-label="Legend" className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              {legend.map(({ label, color }) => {
                const on = activeLegend === label;
                const hoverable = !!onLegendHover;
                return (
                  <li
                    key={label}
                    tabIndex={hoverable ? 0 : undefined}
                    onMouseEnter={hoverable ? () => onLegendHover(label) : undefined}
                    onMouseLeave={hoverable ? () => onLegendHover(null) : undefined}
                    onFocus={hoverable ? () => onLegendHover(label) : undefined}
                    onBlur={hoverable ? () => onLegendHover(null) : undefined}
                    data-testid={hoverable ? `${testId}-legend-${label}` : undefined}
                    className={`flex items-center gap-1.5 rounded px-1 text-xs transition-[opacity,background-color] duration-150 focus:outline-none ${
                      hoverable ? "cursor-default" : ""
                    } ${on ? "bg-theme-hover font-semibold text-content-primary" : "text-content-secondary"} ${
                      activeLegend && !on ? "opacity-40" : ""
                    }`}
                  >
                    <span
                      className={`inline-block shrink-0 rounded-sm transition-transform ${on ? "scale-125" : ""}`}
                      style={{ background: color, width: 10, height: 10 }}
                    />
                    {label}
                  </li>
                );
              })}
            </ul>
          )}
          {table && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-content-secondary">View as table</summary>
              <table className="mt-2 w-full text-left">
                <thead>
                  <tr>
                    {table.columns.map((col) => (
                      <th key={col} className="py-1 pr-3 font-medium text-content-secondary">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {row.map((cell, j) => (
                        <td key={j} className="py-1 pr-3 text-content-primary">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </Card>
  </div>
);

export default SocChartCard;
